import { IPublicClientApplication } from '@azure/msal-browser'
import axios from 'axios'
import { protectedResources } from '../authConfig'
import { Location } from '../models/Location'
import { OnboardingLocation } from '../models/Onboarding/OnboardingLocation'
import {
  authorizedDelete,
  authorizedGet,
  authorizedPatch,
  authorizedPost,
} from '../utils/authUtils'

export const fetchLocations = async (
  categories: number[] | null,
  city: string | null,
  boundingBox: {
    north: number
    south: number
    east: number
    west: number
  } | null = null,
  autoApprovalOnly: boolean = false,
  priceRange: [number | null, number | null] | undefined = undefined,
  businessName: string = '',
  screenSizeRange: [number | null, number | null] | undefined = undefined,
  footTraffic: string[] = [],
  skip: number = 0,
  take: number = 20,
): Promise<{
  locations: Location[]
  totalCount: number
}> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  let url = `${baseUrl}/locations`
  const params: any = {}

  if (categories != null) {
    params.categories = categories.join(',')
  }
  if (city != null) {
    params.cityAndState = city
  }
  if (boundingBox != null) {
    params.northLat = boundingBox.north
    params.southLat = boundingBox.south
    params.eastLng = boundingBox.east
    params.westLng = boundingBox.west
  }
  if (autoApprovalOnly) {
    params.autoApprovalOnly = 'true'
  }

  // Handle monthly price range - only send if values are not null
  if (priceRange != null) {
    if (priceRange[0] != null) {
      params.minMonthlyPrice = priceRange[0]
    }
    if (priceRange[1] != null) {
      params.maxMonthlyPrice = priceRange[1]
    }
  }

  if (businessName.trim()) {
    params.businessName = businessName.trim()
  }

  // Handle screen size range - only send if values are not null
  if (screenSizeRange != null) {
    if (screenSizeRange[0] != null) {
      params.minScreenSize = screenSizeRange[0]
    }
    if (screenSizeRange[1] != null) {
      params.maxScreenSize = screenSizeRange[1]
    }
  }

  // Handle foot traffic filter - only send if array is not empty
  if (footTraffic.length > 0) {
    params.footTraffic = footTraffic.join(',')
  }

  if (skip > 0) {
    params.skip = skip
  }
  if (take !== 20) {
    params.take = take
  }

  const response = await axios.get<{
    locations: Location[]
    totalCount: number
  }>(`${url}?${new URLSearchParams(params).toString()}`)
  return response.data
}

export const fetchLocationsByOrganizationId = async (
  organizationId: number,
): Promise<Location[]> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  let locations = await axios.get(
    `${baseUrl}/organizations/${organizationId}/locations`,
  )
  return locations.data
}

export const fetchLocationById = async (
  id: string,
  instance?: IPublicClientApplication,
): Promise<Location> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  const url = `${baseUrl}/locations/${id}`

  if (instance) {
    return await authorizedGet(instance, url, protectedResources.scopes.read)
  } else {
    const response = await axios.get<Location>(url)
    return response.data
  }
}

export const initializeLocationData = async (
  organizationId: number,
  instance: IPublicClientApplication,
): Promise<OnboardingLocation> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  return await authorizedPost(
    instance,
    `${baseUrl}/locations`,
    { organizationId },
    protectedResources.scopes.read,
  )
}

export const updateLocation = async (
  location: Partial<OnboardingLocation>,
  instance: IPublicClientApplication,
): Promise<OnboardingLocation> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  return await authorizedPatch(
    instance,
    `${baseUrl}/locations/${location.id}`,
    location,
    protectedResources.scopes.read,
  )
}

export interface CityDto {
  id: number
  name: string
  state: string
  latitude: number
  longitude: number
}

export const fetchCityStateCombinations = async (): Promise<CityDto[]> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  const response = await axios.get<CityDto[]>(`${baseUrl}/locations/cities`)
  return response.data
}

export interface CategoryDto {
  id: number
  name: string
}

export const fetchCategories = async (): Promise<CategoryDto[]> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  const response = await axios.get<CategoryDto[]>(`${baseUrl}/categories`)
  return response.data
}

export const deleteLocation = async (
  locationId: number,
  instance: IPublicClientApplication,
): Promise<void> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  await authorizedDelete(
    instance,
    `${baseUrl}/locations/${locationId}`,
    protectedResources.scopes.read,
  )
}

export const reportLocation = async (
  locationId: number,
  reason: string,
  instance: IPublicClientApplication,
): Promise<void> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  await authorizedPost(
    instance,
    `${baseUrl}/locations/${locationId}/report`,
    { reason },
    protectedResources.scopes.read,
  )
}
